/**
 * Shared spreadsheet utilities for cleaning Excel/CSV data.
 *
 * Used by both the frontend manual upload flow and backend workflow
 * steps to ensure consistent data cleaning behavior.
 *
 * The approach: SheetJS `raw: false` gives formatted values (readable dates,
 * formatted numbers). These functions then clean formatted numbers back to
 * actual numbers while preserving dates and text.
 */

/**
 * Clean and parse Excel formatted values, focusing on formatted numbers.
 *
 * Uses raw: false output from SheetJS where:
 * - Numbers come as "1,000" (formatted with commas — needs cleaning)
 * - Dates come as "2024-01-01" (human readable — preserved as-is)
 *
 * Conservative criteria — ALL must be true to clean a value:
 * - Matches strict regex patterns for formatted numbers
 * - Contains only valid number characters
 * - Has ≤ 3 commas, no consecutive commas, no leading/trailing commas
 * - Has ≤ 1 decimal point (rejects European format like 1.000.000)
 *
 * Examples:
 * ✅ "1,000" → 1000
 * ✅ "$1,000" → 1000
 * ✅ "1,000 CAD" → 1000
 * ✅ "-1,000" → -1000
 * ✅ "(1,000)" → -1000 (accounting format)
 * ✅ "25%" → 0.25
 * ✅ "2024-01-01" → "2024-01-01" (date — preserved)
 * ✅ "1/1/2024" → "1/1/2024" (date — preserved)
 * ✅ "1000CAD" → "1000CAD" (no space — preserved as text)
 */
export const cleanExcelValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return value;
    }

    // Percentage format: 25% = 0.25
    const percentageFormatRegex = /^[\d,]+\.?\d*%$/;
    if (percentageFormatRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue.replace(/%/g, "").replace(/,/g, "");
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return parsedNumber / 100;
      }
    }

    // Negative numbers: -1,000 = -1000
    const negativeNumberRegex = /^-[\d,]+\.?\d*$/;
    if (negativeNumberRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue.replace(/,/g, "");
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return parsedNumber;
      }
    }

    // Accounting format: (1,000) = -1000, ($ 773.44) = -773.44, ($$ 773.44) = -773.44
    const accountingFormatRegex = /^\([$€£¥]*\s*[\d,]+\.?\d*\)$/;
    if (accountingFormatRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue
        .replace(/[(),$€£¥,]/g, "")
        .replace(/\s/g, "");
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return -parsedNumber;
      }
    }

    // Formatted numbers with optional currency
    const cleanFormattedNumberRegex = /^[\d,]+\.?\d*$/;
    const cleanCurrencyPrefixRegex = /^[$€£¥]+\s*[\d,]+\.?\d*$/;
    const cleanCurrencySuffixRegex =
      /^[\d,]+\.?\d*\s+(CAD|USD|EUR|GBP|JPY|AUD|CHF|CNY|INR|BRL|MXN|RUB|ZAR|KRW|SGD|HKD|NZD)$/i;

    const hasOnlyValidChars = /^[\d,.\s$€£¥A-Za-z]*$/.test(trimmedValue);
    const hasReasonableCommaCount =
      (trimmedValue.match(/,/g) || []).length <= 3;
    const noConsecutiveCommas = !/,{2,}/.test(trimmedValue);
    const noLeadingTrailingCommas = !/^,|,$/.test(trimmedValue);
    const hasOnlyOneDecimalPoint =
      (trimmedValue.match(/\./g) || []).length <= 1;

    if (
      (cleanFormattedNumberRegex.test(trimmedValue) ||
        cleanCurrencyPrefixRegex.test(trimmedValue) ||
        cleanCurrencySuffixRegex.test(trimmedValue)) &&
      hasOnlyValidChars &&
      hasReasonableCommaCount &&
      noConsecutiveCommas &&
      noLeadingTrailingCommas &&
      hasOnlyOneDecimalPoint
    ) {
      const cleanedValue = trimmedValue
        .replace(/[,$€£¥\s]/g, "")
        .replace(
          /(CAD|USD|EUR|GBP|JPY|AUD|CHF|CNY|INR|BRL|MXN|RUB|ZAR|KRW|SGD|HKD|NZD)$/i,
          "",
        );
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return parsedNumber;
      }
    }

    return value;
  }

  return value;
};

/**
 * Clean all values in a 2D array using cleanExcelValue.
 */
export const cleanDataValues = (data: unknown[][]): unknown[][] => {
  return data.map((row) => row.map((cell) => cleanExcelValue(cell)));
};

/**
 * Filter out completely empty rows and columns from spreadsheet data.
 */
export const filterEmptyRowsAndColumns = (data: unknown[][]): unknown[][] => {
  if (data.length === 0) return data;

  // Filter out empty rows
  const rowsFiltered = data.filter((row) => {
    return row.some((cell) => {
      if (cell === null || cell === undefined) return false;
      if (typeof cell === "string") return cell.trim().length > 0;
      return true;
    });
  });

  if (rowsFiltered.length === 0) return rowsFiltered;

  // Find non-empty columns
  const maxColumns = rowsFiltered.reduce(
    (max, row) => Math.max(max, row.length),
    0,
  );
  const columnsToKeep: number[] = [];
  for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
    const hasNonEmptyCell = rowsFiltered.some((row) => {
      const cell = row[colIndex];
      if (cell === null || cell === undefined) return false;
      if (typeof cell === "string") return cell.trim().length > 0;
      return true;
    });
    if (hasNonEmptyCell) {
      columnsToKeep.push(colIndex);
    }
  }

  // Filter out empty columns
  return rowsFiltered.map((row) => {
    return columnsToKeep.map((colIndex) => {
      const cell = row[colIndex];
      return cell !== undefined && cell !== null ? cell : "";
    });
  });
};

/**
 * Full pipeline: clean Excel values then filter empty rows/columns.
 * Use this after SheetJS `sheet_to_json(sheet, { header: 1, defval: '', raw: false })`.
 */
export const cleanAndFilterSpreadsheetData = (
  data: unknown[][],
): unknown[][] => {
  return filterEmptyRowsAndColumns(cleanDataValues(data));
};

/**
 * Only a value that survives a `Number` -> `String` round trip unchanged.
 * Anchored and without exponent or leading-zero forms on purpose — see
 * {@link toSpreadsheetCellValue}.
 */
const STRICT_NUMBER_RE = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;

/**
 * Type a CSV cell so Excel can actually sum it, without rewriting anything it
 * would have to guess about.
 *
 * A value only becomes a number when it survives a `Number` -> `String` round
 * trip unchanged. That deliberately leaves zero-padded codes ("01234"), ids
 * beyond float precision, trailing-zero decimals ("1.50") and exponent
 * shorthand ("1e5") as the text they were written as, rather than silently
 * corrupting them the way a spreadsheet import usually does.
 *
 * An empty field becomes `null`, not `""`. ExcelJS types `""` as a string and
 * writes it out in full — `<c r="GJ12" t="str"><v></v></c>`, some 37 bytes to
 * say "nothing here" — whereas a null cell is skipped entirely. On a wide
 * export that difference is most of the file: a 192-column vendor pricelist
 * runs about 70% empty, so the padding outweighed the data roughly two to one.
 * It also matches what Excel's own CSV import produces, where a missing field
 * is a blank cell rather than one holding an empty string.
 *
 * Shared by the backend's streaming CSV -> XLSX conversion and the frontend's
 * CSV diff workbook, so a cell exported by either reads the same in Excel.
 */
export const toSpreadsheetCellValue = (
  cell: unknown,
): string | number | null => {
  const value = typeof cell === "string" ? cell : String(cell);
  if (value.length === 0) return null;
  if (!STRICT_NUMBER_RE.test(value)) return value;
  const parsed = Number(value);
  return String(parsed) === value ? parsed : value;
};

/** Excel's own sheet-name rules: at most this long, and none of these. */
export const MAX_SPREADSHEET_SHEET_NAME_LENGTH = 31;
const INVALID_SHEET_NAME_CHARS_RE = /[*?:\\/[\]]/g;
/** Excel allows an apostrophe inside a name but not at either end of one. */
const BOUNDARY_APOSTROPHES_RE = /^'+|'+$/g;
/** Excel keeps this one for its change log and refuses it to any other sheet. */
const RESERVED_SHEET_NAME = "history";

/**
 * A worksheet name Excel will accept.
 *
 * Worth doing even when the name is not user-entered: a translated or
 * interpolated name can carry a colon or run past the length limit, and Excel
 * answers an invalid name by refusing to open the workbook rather than by
 * correcting it. `fallback` is what an empty or wholly invalid name becomes, and
 * takes a value per sheet because a workbook cannot hold two sheets of the same
 * name.
 *
 * The apostrophe and `History` rules are the two that a name taken from a file
 * name reaches on its own — `'summary'.csv` and `history.csv` are both ordinary
 * things to be sent — and neither is a character ExcelJS objects to, so a
 * workbook holding one is written happily and then refused on opening.
 */
const DEFAULT_SHEET_NAME = "Sheet1";

/** The name Excel would accept, or undefined when nothing usable is left. */
const cleanSheetName = (raw: string | undefined): string | undefined => {
  const cleaned = (raw ?? "")
    .replace(INVALID_SHEET_NAME_CHARS_RE, " ")
    .trim()
    // Truncate before stripping apostrophes: the cut can leave one at the end.
    .slice(0, MAX_SPREADSHEET_SHEET_NAME_LENGTH)
    .replace(BOUNDARY_APOSTROPHES_RE, "")
    .trim();
  if (cleaned.length === 0) return undefined;
  return cleaned.toLowerCase() === RESERVED_SHEET_NAME ? undefined : cleaned;
};

export const toSpreadsheetSheetName = (
  raw: string | undefined,
  fallback = DEFAULT_SHEET_NAME,
): string =>
  // The fallback is held to the same rules: a caller reaching for it is already
  // out of ideas, and one that hands over `History` or an overlong name would
  // otherwise get back exactly the name this function exists to rule out.
  cleanSheetName(raw) ?? cleanSheetName(fallback) ?? DEFAULT_SHEET_NAME;
